import { Icon, Tooltip, Markdown, Typography, IconProps } from '@scale/scaleui';
import { CodeBlock } from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/types';
import { twMerge } from 'tailwind-merge';
import { MouseEventHandler, useEffect, useState } from 'react';

interface ICodeContextSidebarProps {
  className?: string;
  codePopupButtonClassName?: string;
  codeBlocks: CodeBlock[];
  codeBlockIndex: number;
  language: string;
  handleSetEditorCode: (code: string) => void;
  handleOpen: (open: boolean) => void;
  open: boolean;
}

export const CodeContextSidebar = ({
  className,
  codePopupButtonClassName,
  codeBlocks,
  codeBlockIndex,
  language,
  handleSetEditorCode,
  handleOpen,
  open,
}: ICodeContextSidebarProps) => {
  const allCode = codeBlocks.reduce((acc, codeBlock) => {
    if (codeBlock.language === language) {
      return acc + codeBlock.code + '\n';
    }
    return acc;
  }, '');

  return (
    <div className={twMerge('flex flex-col bg-[#242424] rounded-b-lg', className)}>
      <div className="flex flex-col w-full h-full pl-1">
        <div className="flex flex-col">
          <div className="flex flex-row justify-between items-center pl-1 pt-1 h-16">
            <div className="flex flex-row items-center">
              <Icon
                icon={['fas', 'code']}
                size="md"
                className="fill-neutral-300/50 stroke-success-500"
                variant="white"
              />
              <Typography.Subtitle4 className="text-neutral-0 pl-1 text-lg">
                Code Snippets
              </Typography.Subtitle4>
            </div>
            <div className="pr-2">
              <Icon
                icon={open ? ['fas', 'chevron-down'] : ['fas', 'chevron-up']}
                size="md"
                className="fill-neutral-300/50 stroke-neutral-300"
                variant="white"
                onClick={() => handleOpen(!open)}
              />
            </div>
          </div>
          <section className="ml-2">
            {open && (
              <CodePopupButton
                className="hover:bg-neutral-700 w-fit flex flex-row items-center"
                variant={'long'}
                code={allCode}
                language={language}
                selected={false}
                title="Replace Current Code With All Code Snippets"
                icon={['fas', 'arrow-right']}
                onClick={() => handleSetEditorCode(allCode)}
              />
            )}
          </section>
        </div>
        <section className="ml-2 mt-4 h-full overflow-y-hidden">
          {open && (
            <div className="flex flex-row overflow-x-auto pb-2">
              {codeBlocks.map((codeBlock, index) => {
                return (
                  <div key={index} className="w-32 h-20 pr-2 shrink-0">
                    <CodePopupButton
                      className={codePopupButtonClassName}
                      code={codeBlock.code}
                      language={codeBlock.language}
                      selected={codeBlockIndex === index}
                      title={`Block ${(index + 1).toString()}`}
                      icon={['far', 'clipboard']}
                      onClickTransitionIcon={['fas', 'check']}
                      onClick={() => {
                        navigator.clipboard.writeText(codeBlock.code);
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

interface ICodePopupButtonProps {
  className?: string;
  code: string;
  language: string;
  selected: boolean;
  title: string;
  onClick?: MouseEventHandler;
  icon?: IconProps['icon'];
  onClickTransitionIcon?: IconProps['icon'];
  variant?: 'long' | 'block';
}

const CodePopupButton = ({
  className,
  code,
  language,
  selected,
  title,
  onClick,
  icon,
  onClickTransitionIcon,
  variant = 'block',
}: ICodePopupButtonProps) => {
  const [clicked, setClicked] = useState<boolean>(false);

  useEffect(() => {
    if (clicked) {
      const timeout = setTimeout(() => setClicked(false), 1000);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [clicked]);

  return (
    <Tooltip
      target={
        <div
          className={twMerge(
            'bg-[#2d2d30] h-full w-full align-middle hover:cursor-pointer rounded-md',
            className,
          )}
        >
          <div
            className={
              variant === 'block'
                ? 'flex flex-col h-full w-full md:flex-wrap justify-between'
                : 'flex items-center'
            }
            onClick={e => {
              onClick && onClick(e);
              setClicked(true);
            }}
          >
            <div className="flex flex-col items-baseline p-2">
              <span className="text-neutral-0 text-xs font-medium">{title}</span>
              {selected && <span className="text-primary-300 text-[9px]"> Current </span>}
            </div>
            <div
              className={twMerge(
                'flex flex-row justify-end pr-1',
                variant === 'block' ? 'pb-1' : '',
              )}
            >
              {!clicked ? (
                icon && (
                  <Icon
                    icon={icon}
                    size="sm"
                    className="fill-neutral-300/50 stroke-success-500"
                    variant="white"
                  />
                )
              ) : onClickTransitionIcon ? (
                <Icon
                  icon={onClickTransitionIcon}
                  size="sm"
                  className="fill-neutral-300/50 stroke-neutral-300"
                  variant="white"
                />
              ) : (
                icon && (
                  <Icon
                    icon={icon}
                    size="sm"
                    className="fill-neutral-300/50 stroke-success-500"
                    variant="white"
                  />
                )
              )}
            </div>
          </div>
        </div>
      }
      side="top"
      delayDuration={0.1}
      text={
        <div className="whitespace-pre-wrap">
          <Markdown markdown={`~~~${language}\n${code}\n~~~`} />
        </div>
      }
    />
  );
};
